import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Users2, Search, Plus, Download, MapPin, Phone, Printer, Mail, Building2,
  ChevronDown, Trash2, X, Bot, Send, Upload, CheckCircle2,
  FileSpreadsheet, FileText, Save, Edit2, AlertTriangle, Loader2, Stethoscope,
  Sparkles, ExternalLink, ShieldCheck,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';
import LegacySpecialtyAuditModal from './referrals/LegacySpecialtyAuditModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = SECTIONS.referrals;

const CATEGORIES = ['GP', 'Physio', 'Dental', 'Podiatrist', 'Chiropractor', 'Osteopath', 'Specialist', 'Other'];
const CAT_COLORS = {
  GP: '#2563EB', Physio: '#0891B2', Dental: '#16A34A', Podiatrist: '#D97706',
  Chiropractor: '#DC2626', Osteopath: '#7C3AED', Specialist: '#EC4899', Other: '#64748B',
};

// ─────────────────────────────────────────────────────────────────────────────
// LIST + SEARCH
// ─────────────────────────────────────────────────────────────────────────────

export function ReferralBasePage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [refs, setRefs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pulseHl, setPulseHl] = useState(false);   // brief flash on KPI when decremented
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeCat, setActiveCat] = useState('');
  const [missing, setMissing] = useState(''); // '' | 'provider_number' | 'healthlink' | 'ahpra'
  const [showChat, setShowChat] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [showLegacyAudit, setShowLegacyAudit] = useState(false);
  const [editing, setEditing] = useState(null); // 'new' | referrer object
  const [uploading, setUploading] = useState(false);
  const [focusOpen, setFocusOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.append('q', q.trim());
      if (activeCat) params.append('category', activeCat);
      if (missing) params.append('missing', missing);
      const [r, s] = await Promise.all([
        axios.get(`${API}/referrals?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/referrals/stats`, { withCredentials: true }),
      ]);
      setRefs(r.data);
      setStats(s.data);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCat, missing]);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const triggerExport = async (fmt) => {
    // Iter 139 — Karisma flagged "cannot export Kestral CSV". Root cause:
    // `window.open(href, '_blank')` opened a new tab without carrying our
    // JWT cookie reliably (Safari ITP strips cross-tab cookies, and the
    // Kestral export requires `require_pm` auth). Switch to an
    // authenticated axios fetch (which routes through our Bearer-token
    // interceptor for Safari) and trigger the download from a Blob URL.
    const params = new URLSearchParams();
    if (activeCat) params.append('category', activeCat);
    const href = `${API}/referrals/export.${fmt}?${params.toString()}`;
    setExportOpen(false);
    try {
      const r = await axios.get(href, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: r.headers['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Derive a sensible filename from the Content-Disposition header
      // when the backend sets one; otherwise fall back to the format.
      const cd = r.headers['content-disposition'] || '';
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      a.download = m ? decodeURIComponent(m[1]) : `referrals.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 401 || status === 403
        ? 'You need Practice Manager access to run this export.'
        : `Export failed (${status || 'network'}) — please try again.`;
      // eslint-disable-next-line no-alert
      alert(msg);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="referrals-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title row */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
              style={{ backgroundColor: R.colorBg, color: R.color }}>
              <Users2 className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Referral Base</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground">Referring clinics</h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Live directory of every clinic and practitioner the practice attracts referrals from. Search, filter, ask the assistant, or export the whole base.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isPM && (
              <>
                <button onClick={() => setUploading(true)} data-testid="referrals-upload-btn"
                  title="Drop the i3 Marketing Excel — bulk-imports every sheet, idempotent"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
                  style={{ borderColor: `${R.color}40`, color: R.color }}>
                  <Upload className="w-4 h-4" /> Import Excel
                </button>
                <button onClick={() => setEditing('new')} data-testid="referrals-new-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
                  style={{ backgroundColor: R.color }}>
                  <Plus className="w-4 h-4" /> Add referrer
                </button>
              </>
            )}
            <div className="relative">
              <button onClick={() => setExportOpen(o => !o)} data-testid="referrals-export-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border"
                style={{ borderColor: `${R.color}40`, color: R.color }}>
                <Download className="w-4 h-4" /> Export <ChevronDown className="w-3 h-3" />
              </button>
              {exportOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-xl shadow-lg z-20 overflow-hidden" data-testid="referrals-export-menu">
                  <button onClick={() => triggerExport('pdf')} data-testid="referrals-export-pdf"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
                    <FileText className="w-4 h-4" style={{ color: '#DC2626' }} /> PDF
                  </button>
                  <button onClick={() => triggerExport('xlsx')} data-testid="referrals-export-xlsx"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4" style={{ color: '#16A34A' }} /> Excel (.xlsx)
                  </button>
                  <button onClick={() => triggerExport('csv')} data-testid="referrals-export-csv"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4" style={{ color: '#0891B2' }} /> CSV
                  </button>
                  {/* Iter 87 — letterhead generator now lives as a small icon
                      in Practice Settings (iter 91), removed from this menu
                      to keep the exports dropdown focused on data exports. */}
                  {isPM && (
                    <button onClick={() => triggerExport('kestral.zip')} data-testid="referrals-export-kestral"
                      title="Bundle of 6 CSVs ready to import into Kestral / Karisma — Practitioner, Speciality reference, Location, Assignment, and the two HealthLink alias templates."
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 border-t border-border">
                      <FileSpreadsheet className="w-4 h-4" style={{ color: '#A855F7' }} />
                      <span className="flex-1">Kestral / Karisma bundle</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#A855F715', color: '#A855F7' }}>6 CSV</span>
                    </button>
                  )}
                  {isPM && (
                    <button onClick={() => { setShowLegacyAudit(true); setExportOpen(false); }}
                      data-testid="referrals-legacy-specialties"
                      title="Audit DB for non-Kestral specialty codes and optionally migrate them"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" style={{ color: '#0d2644' }} />
                      <span className="flex-1">Audit specialty codes</span>
                    </button>
                  )}
                  <div className="text-[10px] text-muted-foreground px-3 py-2 border-t border-border">
                    {activeCat ? `Filtering: ${activeCat}` : 'Full base'}
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => setShowChat(true)} data-testid="referrals-chat-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
              style={{ backgroundColor: '#0d2644' }}>
              <Bot className="w-4 h-4" /> Ask assistant
            </button>
            {isPM && <BulkAutoFillButton activeCat={activeCat} onDone={load} R={R} />}
          </div>
        </div>

        {/* Stats strip — focused on what actually matters: practitioner count
            and the two compliance gaps (Provider # is mandatory for billing,
            HealthLink is mandatory for e-referrals). Tiles double as filters. */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="referrals-stats">
            <KpiBig label="Practitioners" value={stats.total_practitioners} color="#16A34A"
              testid="kpi-practitioners" />
            <KpiBig label="Without provider #" value={stats.missing_provider_number ?? 0}
              sublabel={`across ${stats.clinics_missing_provider_number ?? 0} clinics`}
              color="#DC2626" active={missing === 'provider_number'}
              onClick={() => setMissing(missing === 'provider_number' ? '' : 'provider_number')}
              testid="kpi-missing-provider" />
            <div className={pulseHl ? 'transition-all duration-500 scale-[1.04] ring-2 ring-amber-400/60 rounded-2xl' : 'transition-all duration-500'}>
              <KpiBig label="Without HealthLink" value={stats.missing_healthlink ?? 0}
                sublabel={`across ${stats.clinics_missing_healthlink ?? 0} clinics`}
                color="#D97706" active={missing === 'healthlink'}
                onClick={() => setMissing(missing === 'healthlink' ? '' : 'healthlink')}
                testid="kpi-missing-healthlink" />
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 mb-5" data-testid="referrals-filters">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input type="search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search by clinic, practitioner, suburb, ID — wildcards: John* or *Stevens"
              data-testid="referrals-search"
              className="flex-1 min-w-[200px] bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground" />
            {q && <button onClick={() => setQ('')} className="text-xs text-muted-foreground hover:text-foreground">clear</button>}
            <select value={missing} onChange={e => setMissing(e.target.value)}
              data-testid="referrals-missing-filter"
              title="Surface clinics with practitioners that are missing key IDs"
              className="text-xs font-semibold bg-card border border-border rounded-lg px-2.5 py-1.5 text-foreground outline-none cursor-pointer"
              style={missing ? { borderColor: `${R.color}80`, color: R.color, backgroundColor: `${R.color}10` } : undefined}>
              <option value="">Show all</option>
              <option value="provider_number">Missing Provider #</option>
              <option value="ahpra">Missing AHPRA</option>
              <option value="healthlink">Missing HealthLink</option>
            </select>
            {isPM && missing && (
              <button onClick={() => setFocusOpen(true)} data-testid="referrals-focus-mode-btn"
                title="Open the missing-IDs focus drawer — fix every gap in one scrollable list"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg text-white"
                style={{ backgroundColor: R.color }}>
                <Sparkles className="w-3.5 h-3.5" /> Focus mode
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap border-t border-border pt-3">
            <CatPill label={`All · ${stats?.total ?? '—'}`} active={!activeCat} onClick={() => setActiveCat('')} testid="referrals-cat-all" />
            {CATEGORIES.map(c => (
              <CatPill key={c}
                label={`${c} · ${stats?.by_category?.[c] ?? 0}`}
                color={CAT_COLORS[c]}
                active={activeCat === c}
                onClick={() => setActiveCat(activeCat === c ? '' : c)}
                testid={`referrals-cat-${c}`} />
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-16 text-sm text-muted-foreground">Loading referrers…</div>
        ) : refs.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground" data-testid="referrals-empty">
            No referrers match. Try a wildcard like <code className="font-mono bg-muted px-1 rounded">John*</code> or clear filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="referrals-list">
            {refs.map(r => <ReferrerCard key={r.referrer_id} r={r}
              onEdit={() => setEditing(r)} onDelete={async () => {
                if (!window.confirm(`Delete ${r.clinic_name}?`)) return;
                await axios.delete(`${API}/referrals/${r.referrer_id}`, { withCredentials: true });
                load();
              }} isPM={isPM} />)}
          </div>
        )}
      </div>

      {/* Persistent floating chat bubble (matches SOPs pattern). Hidden when
          chat panel is already open — the panel itself replaces the bubble. */}
      {!showChat && (
        <button onClick={() => setShowChat(true)} data-testid="referrals-chat-bubble"
          className="fixed bottom-20 right-6 z-40 w-14 h-14 text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all hover:scale-105 group"
          style={{ backgroundColor: R.color }}>
          <Bot className="w-6 h-6" />
          <span className="absolute -top-10 right-0 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Referral Base Assist
          </span>
        </button>
      )}

      {/* Chat panel (floating, SOP-style — taller + wider variant) */}
      {showChat && <ReferralChat onClose={() => setShowChat(false)} />}

      {/* Editor modal */}
      {editing && <ReferrerEditor
        initial={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        onHealthlinkVerified={() => {
          // Optimistic KPI tile decrement so PMs get instant feedback
          // without a full reload. Reload syncs the authoritative count.
          setStats(s => s ? { ...s, missing_healthlink: Math.max(0, (s.missing_healthlink ?? 1) - 1) } : s);
          setPulseHl(true);
          setTimeout(() => setPulseHl(false), 900);
        }}
      />}

      {/* Excel upload modal */}
      {uploading && <ExcelUploadModal
        onClose={() => setUploading(false)}
        onDone={() => { setUploading(false); load(); }}
      />}

      {/* Focus-mode drawer — surfaces every practitioner missing the active
          ID field in one scrollable list with inline edit + RCH lookup. */}
      {focusOpen && (
        <FocusModeDrawer
          field={missing || 'provider_number'}
          onClose={() => { setFocusOpen(false); load(); }}
          R={R}
        />
      )}
      {showLegacyAudit && (
        <LegacySpecialtyAuditModal
          onClose={() => setShowLegacyAudit(false)}
          onMigrated={load} />
      )}
    </div>
  );
}

/** Bigger, optionally-clickable KPI tile used on the Referral Base header.
 *  Clickable variants act as "missing-X" filter toggles. */
function KpiBig({ label, value, sublabel, color, active, onClick, testid }) {
  const clickable = typeof onClick === 'function';
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag onClick={onClick} data-testid={testid}
      className={`bg-card border rounded-2xl px-5 py-4 text-left transition-all ${clickable ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer' : ''}`}
      style={active
        ? { borderColor: `${color}80`, backgroundColor: `${color}10`, boxShadow: `0 0 0 2px ${color}30` }
        : { borderColor: 'hsl(var(--border))' }}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-['Outfit'] text-4xl sm:text-5xl font-bold mt-1 tabular-nums" style={{ color }}>{value}</p>
      {sublabel && <p className="text-[11px] text-muted-foreground mt-1">{sublabel}{clickable && (active ? ' · click to clear' : ' · click to filter')}</p>}
      {!sublabel && clickable && <p className="text-[11px] text-muted-foreground mt-1">{active ? 'click to clear' : 'click to filter'}</p>}
    </Tag>
  );
}

function CatPill({ label, color, active, onClick, testid }) {
  const c = color || R.color;
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
      style={active
        ? { backgroundColor: `${c}15`, borderColor: `${c}40`, color: c }
        : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
      {label}
    </button>
  );
}

function ReferrerCard({ r, onEdit, onDelete, isPM }) {
  const [open, setOpen] = useState(false);
  const cat = CAT_COLORS[r.category] || '#64748B';
  const prac = r.practitioners || [];
  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex flex-col gap-2" data-testid={`referrer-card-${r.referrer_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${cat}15`, color: cat }}>
              {r.category}
            </span>
            {r.suburb && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {r.suburb}
            </span>}
            {r.speciality && <span className="text-[10px] font-semibold" style={{ color: '#7C3AED' }}>{r.speciality}</span>}
          </div>
          <h3 className="font-['Outfit'] text-base font-semibold text-foreground leading-tight whitespace-pre-wrap">{r.clinic_name}</h3>
        </div>
        {isPM && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={onEdit} className="p-1.5 text-muted-foreground hover:text-foreground" title="Edit"
              data-testid={`referrer-edit-${r.referrer_id}`}><Edit2 className="w-4 h-4" /></button>
            <button onClick={onDelete} className="p-1.5 text-muted-foreground hover:text-red-500" title="Delete"
              data-testid={`referrer-delete-${r.referrer_id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {r.address && <p className="text-xs text-muted-foreground inline-flex items-start gap-1 whitespace-pre-wrap">
        <Building2 className="w-3 h-3 mt-0.5 shrink-0" /> {r.address}
      </p>}

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
        {r.fax && <span className="inline-flex items-center gap-1"><Printer className="w-3 h-3" /> {r.fax}</span>}
        {r.pm_email && <a href={`mailto:${r.pm_email}`} className="inline-flex items-center gap-1 hover:text-foreground">
          <Mail className="w-3 h-3" /> {r.pm_email}
        </a>}
      </div>

      {r.clinic_healthlink_id && (
        <div className="text-[10px]"><span className="text-muted-foreground">HealthLink:</span> <code className="font-mono">{r.clinic_healthlink_id}</code></div>
      )}

      {(prac.length > 0 || r.practitioner_count) && (
        <div className="border-t border-border pt-2 mt-1">
          <button onClick={() => setOpen(o => !o)}
            data-testid={`referrer-prac-toggle-${r.referrer_id}`}
            className="text-[11px] font-semibold flex items-center gap-1 w-full justify-between hover:text-foreground"
            style={{ color: R.color }}>
            <span>Practitioners ({prac.length || `~${r.practitioner_count}`})</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <ul className="mt-2 text-xs space-y-1 max-h-48 overflow-y-auto pr-1">
              {prac.map((p, i) => (
                <li key={i} className="leading-tight">
                  <div className="font-medium text-foreground flex items-center gap-1.5">
                    <span>{p.name}</span>
                    {p.ris_code && (
                      <span className="text-[9px] font-mono font-bold tracking-wider bg-purple-500/15 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded"
                        data-testid={`ref-list-ris-${r.referrer_id}-${i}`}>
                        {p.ris_code}
                      </span>
                    )}
                  </div>
                  {(p.provider_number || p.healthlink_id || p.kestrel_code || p.ahpra_id) && (
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {p.provider_number && <span>PN:{p.provider_number} </span>}
                      {p.ahpra_id && <span>AHPRA:{p.ahpra_id} </span>}
                      {p.healthlink_id && <span>HL:{p.healthlink_id} </span>}
                      {p.kestrel_code && <span>K:{p.kestrel_code}</span>}
                    </div>
                  )}
                </li>
              ))}
              {prac.length === 0 && r.practitioner_count && (
                <li className="text-muted-foreground italic">~{r.practitioner_count} practitioners (names not yet captured)</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHATBOT DRAWER
// ─────────────────────────────────────────────────────────────────────────────

function ReferralChat({ onClose }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi — I can answer anything about your referral base. Try:\n• \"Where does Dr John Wighton practice?\"\n• \"List all Stevens.\"\n• \"How many physios in Dapto?\"\nWildcards (*) supported." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }, { role: 'assistant', content: '…', pending: true }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/referrals/chat`,
        { message: text, history: messages.filter(m => !m.pending) },
        { withCredentials: true, timeout: 60000 });
      setMessages(m => [...m.filter(x => !x.pending), { role: 'assistant', content: r.data.reply }]);
    } catch (e) {
      setMessages(m => [...m.filter(x => !x.pending),
        { role: 'assistant', content: `Sorry — ${e?.response?.data?.detail || e.message || 'something went wrong'}.` }]);
    }
    setBusy(false);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const SUGGESTIONS = [
    "Where does Dr John Wighton practice?",
    "List every John* in the database",
    "How many physios in Dapto?",
    "Show all Stevens with phone numbers",
    "Specialists in Wollongong",
  ];

  return (
    <div data-testid="referrals-chat"
      className="fixed bottom-20 right-6 z-50 w-[640px] h-[840px] max-h-[90vh] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
      style={{ maxWidth: 'calc(100vw - 32px)' }}>
      {/* Header */}
      <div className="text-white px-4 py-3 flex items-center justify-between shrink-0"
        style={{ background: `linear-gradient(135deg, ${R.color} 0%, #0d2644 100%)` }}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Bot className="w-4 h-4" /></div>
          <div>
            <h3 className="text-sm font-semibold font-['Outfit']">Referral Base Assist</h3>
            <p className="text-[10px] opacity-75">GPT-5 mini · {messages.filter(m => !m.pending).length - 1} turn{messages.filter(m => !m.pending).length === 2 ? '' : 's'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMessages(messages.slice(0, 1))} data-testid="referrals-chat-clear"
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" title="Clear conversation">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={onClose} data-testid="referrals-chat-close"
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/10" data-testid="referrals-chat-log">
        {messages.length <= 1 ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${R.color}15` }}>
              <Bot className="w-6 h-6" style={{ color: R.color }} />
            </div>
            <h4 className="font-['Outfit'] text-sm font-semibold text-foreground mb-1">Referral Base Assist</h4>
            <p className="text-xs text-muted-foreground mb-4 max-w-md mx-auto whitespace-pre-wrap">
              {messages[0].content}
            </p>
            <div className="space-y-2 max-w-md mx-auto">
              {SUGGESTIONS.map((q, i) => (
                <button key={i} onClick={() => send(q)}
                  data-testid={`referrals-chat-suggestion-${i}`}
                  className="block w-full text-left px-3 py-2 text-xs text-foreground/80 bg-card hover:text-white rounded-lg transition-colors border border-border"
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = R.color}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                style={m.role === 'user'
                  ? { backgroundColor: 'hsl(var(--muted))' }
                  : { backgroundColor: `${R.color}15` }}>
                <Bot className="w-3.5 h-3.5" style={m.role !== 'user' ? { color: R.color } : { color: 'hsl(var(--muted-foreground))' }} />
              </div>
              <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.pending ? 'animate-pulse' : ''} ${m.role === 'user' ? 'text-white rounded-tr-md' : 'bg-muted text-foreground rounded-tl-md'}`}
                style={m.role === 'user' ? { backgroundColor: R.color } : {}}>
                {m.content}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <form onSubmit={e => { e.preventDefault(); send(); }} className="p-3 border-t border-border shrink-0">
        <div className="flex gap-2">
          <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)}
            disabled={busy}
            data-testid="referrals-chat-input"
            placeholder="Ask about a doctor, clinic, suburb…"
            className="flex-1 px-4 py-2.5 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 disabled:opacity-50"
            style={{ '--tw-ring-color': `${R.color}30` }} />
          <button type="submit" disabled={busy || !input.trim()}
            data-testid="referrals-chat-send"
            className="px-3 py-2.5 text-white rounded-xl transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
            style={{ backgroundColor: busy || !input.trim() ? undefined : R.color }}>
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EDITOR
// ─────────────────────────────────────────────────────────────────────────────

const blankPrac = () => ({ name: '', prefix: '', first_name: '', last_name: '', provider_number: '', ahpra_id: '', hpi_i: '', healthlink_id: '', kestrel_code: '', specialty: '', email: '', mobile: '', notes: '' });

function ReferrerEditor({ initial, onClose, onSaved, onHealthlinkVerified }) {
  const [form, setForm] = useState(() => initial ? { ...initial, practitioners: [...(initial.practitioners || [])] } : {
    category: 'GP', clinic_name: '', suburb: '', state: 'NSW', postcode: '',
    address: '', phone: '', fax: '', pm_email: '', speciality: '',
    practitioner_count: null, clinic_healthlink_id: '', notes: '', tags: [],
    practitioners: [], last_meeting_at: '', last_contact_at: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // Lazy-load Kestral specialties (73 official codes) for the per-practitioner dropdown.
  const [specialties, setSpecialties] = useState([]);
  useEffect(() => {
    axios.get(`${API}/referrals/kestral-specialties`, { withCredentials: true })
      .then(r => setSpecialties([{ value: '', label: '— Select Kestral specialty —' }, ...r.data.map(s => ({ value: s.code, label: s.name }))]))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPrac = (i, k, v) => setForm(f => ({ ...f, practitioners: f.practitioners.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const addPrac = () => setForm(f => ({ ...f, practitioners: [...f.practitioners, blankPrac()] }));
  const removePrac = (i) => setForm(f => ({ ...f, practitioners: f.practitioners.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setErr('');
    if (!form.clinic_name.trim()) { setErr('Clinic name is required.'); return; }
    if (!CATEGORIES.includes(form.category)) { setErr('Invalid category.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, practitioner_count: form.practitioner_count ? Number(form.practitioner_count) : null };
      if (initial?.referrer_id) {
        await axios.put(`${API}/referrals/${initial.referrer_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/referrals`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="referrer-editor">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-2xl border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border px-5 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-bold text-foreground">{initial ? 'Edit referrer' : 'New referrer'}</h3>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {err && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-500 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{err}
          </div>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" value={form.category} onChange={v => set('category', v)} testid="ref-cat" type="select" options={CATEGORIES} />
            <Field label="Clinic name *" value={form.clinic_name} onChange={v => set('clinic_name', v)} testid="ref-name" />
            <Field label="Suburb" value={form.suburb} onChange={v => set('suburb', v)} testid="ref-suburb" />
            <Field label="Postcode" value={form.postcode} onChange={v => set('postcode', v)} testid="ref-postcode" />
            <div className="col-span-2"><Field label="Address" value={form.address} onChange={v => set('address', v)} testid="ref-address" /></div>
            <Field label="Phone" value={form.phone} onChange={v => set('phone', v)} testid="ref-phone" />
            <Field label="Fax" value={form.fax} onChange={v => set('fax', v)} testid="ref-fax" />
            <Field label="Practice manager email" value={form.pm_email} onChange={v => set('pm_email', v)} testid="ref-pm" />
            <Field label="Clinic HealthLink EDI" value={form.clinic_healthlink_id} onChange={v => set('clinic_healthlink_id', v)} testid="ref-hlink" />
            {form.category === 'Specialist' && (
              <Field label="Speciality" value={form.speciality} onChange={v => set('speciality', v)} testid="ref-spec" />
            )}
            <Field label="Approx practitioner count" value={form.practitioner_count ?? ''} onChange={v => set('practitioner_count', v)} testid="ref-count" />
          </div>

          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Practitioners</span>
              <button onClick={addPrac} type="button" data-testid="ref-prac-add"
                className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border"
                style={{ borderColor: `${R.color}40`, color: R.color }}>
                <Plus className="w-3 h-3" /> Add practitioner
              </button>
            </div>
            <div className="space-y-3">
              {form.practitioners.length === 0 && <p className="text-xs text-muted-foreground italic">No practitioners added yet.</p>}
              {form.practitioners.map((p, i) => (
                <div key={i} className="border border-border rounded-lg p-3 grid grid-cols-2 gap-2 relative" data-testid={`ref-prac-${i}`}>
                  <button type="button" onClick={() => removePrac(i)}
                    className="absolute top-1 right-1 p-1 text-muted-foreground hover:text-red-500"
                    data-testid={`ref-prac-remove-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                  <div className="col-span-2 grid grid-cols-12 gap-2">
                    <div className="col-span-3"><Field label="Prefix" value={p.prefix} onChange={v => setPrac(i, 'prefix', v)} testid={`ref-prac-prefix-${i}`} /></div>
                    <div className="col-span-4"><Field label="First name" value={p.first_name} onChange={v => setPrac(i, 'first_name', v)} testid={`ref-prac-first-${i}`} /></div>
                    <div className="col-span-5"><Field label="Last name *" value={p.last_name} onChange={v => setPrac(i, 'last_name', v)} testid={`ref-prac-last-${i}`} /></div>
                  </div>
                  <div className="col-span-2"><Field label="Display name (auto if blank)" value={p.name} onChange={v => setPrac(i, 'name', v)} testid={`ref-prac-name-${i}`} /></div>
                  <ProviderNumberField
                    p={p} idx={i} setPrac={setPrac}
                    referrerId={initial?.referrer_id}
                    onPracReplaced={(updatedPrac) => setForm(f => {
                      const next = [...(f.practitioners || [])];
                      next[i] = updatedPrac;
                      return { ...f, practitioners: next };
                    })}
                  />
                  <div className="col-span-2 sm:col-span-1">
                    <Field label="AHPRA ID" value={p.ahpra_id} onChange={v => setPrac(i, 'ahpra_id', v)} testid={`ref-prac-ahpra-${i}`} />
                    <AhpraRow
                      practitioner={p}
                      practitionerIndex={i}
                      referrerId={initial?.referrer_id}
                      onVerified={(blob) => {
                        setPrac(i, 'ahpra_id', blob.ahpra_id);
                        setPrac(i, 'ahpra_verified', blob);
                      }}
                    />
                  </div>
                  <Field label="HPI-I (16 digit)" value={p.hpi_i} onChange={v => setPrac(i, 'hpi_i', v)} testid={`ref-prac-hpii-${i}`} />
                  <div className="col-span-2 sm:col-span-1">
                    <Field label="HealthLink EDI" value={p.healthlink_id} onChange={v => setPrac(i, 'healthlink_id', v)} testid={`ref-prac-hlink-${i}`} />
                    <HealthLinkRow
                      practitioner={p}
                      practitionerIndex={i}
                      referrerId={initial?.referrer_id}
                      onVerified={(blob) => {
                        const wasMissing = !(p.healthlink_id || '').trim();
                        setPrac(i, 'healthlink_id', blob.edi);
                        setPrac(i, 'healthlink_verified', blob);
                        if (wasMissing) onHealthlinkVerified?.();
                      }}
                    />
                  </div>
                  <div className="relative">
                    <Field label="Kestrel / RIS code (auto)" value={p.kestrel_code || p.ris_code || ''} onChange={v => setPrac(i, 'kestrel_code', v)} testid={`ref-prac-kestrel-${i}`} />
                    {p.ris_code && p.kestrel_code && p.kestrel_code !== p.ris_code && (
                      <button type="button"
                        onClick={() => setPrac(i, 'kestrel_code', p.ris_code)}
                        data-testid={`ref-prac-kestrel-reset-${i}`}
                        title={`Restore the auto-derived code (${p.ris_code})`}
                        className="absolute right-2 top-7 text-[10px] font-semibold uppercase tracking-wider text-purple-700 hover:text-purple-900 dark:text-purple-300 dark:hover:text-purple-100 underline-offset-2 hover:underline">
                        Reset to auto
                      </button>
                    )}
                  </div>
                  <Field label="Kestral specialty"  value={p.specialty}    onChange={v => setPrac(i, 'specialty', v)}    testid={`ref-prac-specialty-${i}`} type="select" options={specialties} />
                  <Field label="Email" value={p.email} onChange={v => setPrac(i, 'email', v)} testid={`ref-prac-email-${i}`} />
                  <Field label="Mobile" value={p.mobile} onChange={v => setPrac(i, 'mobile', v)} testid={`ref-prac-mobile-${i}`} />
                  <div className="col-span-2"><Field label="Notes" value={p.notes} onChange={v => setPrac(i, 'notes', v)} testid={`ref-prac-notes-${i}`} /></div>
                </div>
              ))}
            </div>
          </div>

          <div><Field label="Notes" value={form.notes} onChange={v => set('notes', v)} testid="ref-notes" type="textarea" /></div>
        </div>
        <div className="sticky bottom-0 bg-background border-t border-border px-5 py-3 flex justify-end gap-2 z-10">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="ref-save-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: R.color }}>
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (initial ? 'Save changes' : 'Add referrer')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = 'text', options }) {
  const cls = "w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2";
  // options can be either string[] or [{value,label}] — we coerce to the latter.
  const opts = (options || []).map(o => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      {type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls}>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} rows={2} />
      ) : (
        <input type="text" value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} />
      )}
    </label>
  );
}

export default ReferralBasePage;

// ─────────────────────────────────────────────────────────────────────────────
// EXCEL UPLOAD MODAL
// Drag-and-drop the i3 Marketing spreadsheet — backend parses every recognised
// sheet (GP, AH PHYSIO, AH DENTAL, AH PODIATRIST, AH CHIRO, AH OSTEO, SPECIALISTS,
// PAST WOO ENG) and upserts each clinic by (category, clinic_name, suburb).
// Idempotent — re-running just merges new rows.
// ─────────────────────────────────────────────────────────────────────────────

function ExcelUploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState('merge'); // 'merge' (default) | 'skip'
  const inputRef = useRef();

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setResult(null);
      setErr('');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) {
      setFile(f);
      setResult(null);
      setErr('');
    }
  };

  const submit = async () => {
    if (!file || busy) return;
    setBusy(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Let the browser set the multipart boundary itself — explicitly
      // setting Content-Type breaks the upload because the boundary token
      // never makes it into the header.
      const r = await axios.post(`${API}/referrals/upload-xlsx?mode=${mode}`, fd, {
        withCredentials: true,
        timeout: 180000,
      });
      setResult(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 401 || status === 403) {
        msg = 'Your session has expired. Refresh the page, log in again, then re-try the upload.';
      } else if (status === 413) {
        msg = `The server refused the upload because the file (${(file.size / 1024).toFixed(0)} KB) is over the platform limit. Try splitting it into smaller files or contact support to raise the ingress payload cap.`;
      } else if (status === 400 && detail) {
        msg = detail;
      } else if (!status && /timeout/i.test(e?.message || '')) {
        msg = 'Upload timed out after 3 minutes. The file may be very large, or the server is slow — try again, or split the spreadsheet by sheet.';
      } else if (!status) {
        msg = 'Network error — could not reach the server. Check your connection, then try again.';
      } else {
        msg = detail || e.message || `Upload failed (HTTP ${status})`;
      }
      setErr(msg);
      console.error('Excel upload failed', { status, detail, error: e });
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="referrals-upload-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background rounded-2xl border border-border w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-foreground">Bulk-import referrers from Excel</h3>
          <button onClick={onClose} disabled={busy} className="p-1 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!result ? (
            <>
              <p className="text-xs text-muted-foreground">
                Drop the <strong>i3 Marketing</strong> spreadsheet (or any future update). The backend reads every
                recognised sheet — <code className="font-mono text-[10px]">GP, AH PHYSIO, AH DENTAL, AH PODIATRIST,
                AH CHIRO, AH OSTEO, SPECIALISTS, PAST WOO ENG</code> — and applies each clinic according to the
                duplicate handling you choose below.
              </p>

              <div className="border border-border rounded-xl overflow-hidden" data-testid="referrals-upload-mode">
                <div className="px-3 py-2 bg-muted/40 border-b border-border">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">If a clinic already exists</span>
                </div>
                <label className="flex items-start gap-3 px-3 py-2.5 border-b border-border/40 cursor-pointer hover:bg-muted/30">
                  <input type="radio" name="dup-mode" value="merge" checked={mode === 'merge'}
                    onChange={() => setMode('merge')} disabled={busy}
                    data-testid="referrals-upload-mode-merge"
                    className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-foreground">Merge updates <span className="text-[10px] font-normal text-muted-foreground ml-1">(default)</span></div>
                    <div className="text-[11px] text-muted-foreground">
                      Refresh phone, fax, address, PM email from the Excel. Append any new practitioner names. Existing practitioner detail (provider numbers, HealthLink IDs, etc.) is preserved.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/30">
                  <input type="radio" name="dup-mode" value="skip" checked={mode === 'skip'}
                    onChange={() => setMode('skip')} disabled={busy}
                    data-testid="referrals-upload-mode-skip"
                    className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-foreground">Skip duplicates <span className="text-[10px] font-normal" style={{ color: '#D97706' }}>(only insert new)</span></div>
                    <div className="text-[11px] text-muted-foreground">
                      Leave existing clinics completely untouched — only insert genuinely new ones. Use this when you've hand-edited records and don't want a re-uploaded spreadsheet to overwrite your changes.
                    </div>
                  </div>
                </label>
              </div>

              <div onDragOver={e => e.preventDefault()} onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                data-testid="referrals-upload-dropzone"
                className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/60 cursor-pointer transition-colors">
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                {file ? (
                  <>
                    <p className="text-sm font-semibold text-foreground" data-testid="referrals-upload-filename">{file.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB · click to change</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-foreground">Drop or click to pick .xlsx</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Excel 2007+ format only</p>
                  </>
                )}
                <input type="file" ref={inputRef} accept=".xlsx,.xlsm" className="hidden"
                  data-testid="referrals-upload-file-input" onChange={onPick} />
              </div>

              {err && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-500 flex items-start gap-2"
                  data-testid="referrals-upload-error">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{err}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button onClick={onClose} disabled={busy} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
                <button onClick={submit} disabled={!file || busy} data-testid="referrals-upload-submit"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
                  style={{ backgroundColor: R.color }}>
                  <Upload className="w-3.5 h-3.5" /> {busy ? 'Importing…' : 'Import'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border p-4" data-testid="referrals-upload-result"
                style={{ borderColor: '#16A34A40', backgroundColor: '#16A34A10' }}>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#16A34A' }} />
                  <div className="text-sm flex-1">
                    <p className="font-semibold text-foreground">Import complete · {result.filename}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Mode: <strong className="text-foreground capitalize">{result.mode || 'merge'}</strong> ·
                      <strong className="text-foreground"> {result.total_inserted}</strong> new ·{' '}
                      {result.mode === 'skip' ? (
                        <>
                          <strong style={{ color: '#D97706' }}>{result.total_skipped_duplicates ?? 0}</strong> duplicates skipped ·
                        </>
                      ) : (
                        <>
                          <strong className="text-foreground">{result.total_updated}</strong> updated ·
                        </>
                      )}
                      {result.total_skipped > 0 && (
                        <> <strong className="text-foreground">{result.total_skipped}</strong> empty rows ·</>
                      )}
                      <strong className="text-foreground"> {result.total_in_db}</strong> total in database
                    </p>
                  </div>
                </div>
              </div>

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="px-3 py-2 bg-muted/40 border-b border-border">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Per-sheet breakdown</span>
                </div>
                <ul className="text-xs">
                  {Object.entries(result.by_sheet || {}).map(([sheet, info]) => (
                    <li key={sheet} className="px-3 py-2 border-b border-border/40 last:border-0 flex items-center justify-between gap-2">
                      <div>
                        <span className="font-semibold text-foreground">{sheet}</span>
                        {info.category && <span className="ml-2 text-[10px] text-muted-foreground">→ {info.category}</span>}
                      </div>
                      {info.status === 'skipped' ? (
                        <span className="text-[11px] text-amber-600">skipped ({info.reason})</span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground font-mono">
                          +{info.inserted} new · {(result.mode === 'skip' && (info.skipped_duplicates ?? 0) > 0)
                            ? <span style={{ color: '#D97706' }}>~{info.skipped_duplicates} dup-skipped</span>
                            : <span>~{info.updated} updated</span>}
                          {info.skipped > 0 && <> · {info.skipped} empty</>}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button onClick={onDone} data-testid="referrals-upload-done"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white"
                  style={{ backgroundColor: R.color }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Provider Number editor — same shape as <Field> but with a "Look up" button
 * that calls the public RCH search and an inline picker for the candidate
 * list. If the practitioner row has a `medicare_lookup` blob with status
 * `needs_review`, a yellow chip + count is rendered next to the input.
 *
 * Two modes:
 *   • New referrer (no referrerId yet) — Look up still works (proxies to
 *     /api/medicare/search) but result is just used to fill the input
 *     locally; the medicare_lookup audit blob is filled when the parent saves.
 *   • Existing referrer (referrerId present) — uses the dedicated
 *     practitioner-scoped endpoint so the audit blob is persisted server-side.
 */
function ProviderNumberField({ p, idx, setPrac, referrerId, onPracReplaced }) {
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(null);
  const [err, setErr] = useState(null);
  const ml = p.medicare_lookup || {};
  const reviewCount = ml.status === 'needs_review' ? (ml.candidates_count || ml.alternates?.length || 0) : 0;

  const lookup = async () => {
    setBusy(true); setErr(null);
    try {
      if (referrerId !== undefined && referrerId !== null && referrerId !== '') {
        const r = await axios.post(`${API}/referrals/${referrerId}/practitioners/${idx}/medicare-lookup`, {}, { withCredentials: true });
        const lk = r.data.lookup || {};
        if (lk.status === 'auto_high_confidence') {
          setPrac(idx, 'provider_number', r.data.provider_number);
          setPicker(null);
        } else if ((lk.alternates || []).length > 0) {
          setPicker({ results: lk.alternates, total: lk.candidates_count });
        } else if (lk.status === 'no_match') {
          setErr('No match — try editing the surname or first name and retry.');
        } else if (lk.status === 'error') {
          setErr(`Lookup error: ${lk.error}`);
        }
        if (onPracReplaced) {
          onPracReplaced({ ...p, provider_number: r.data.provider_number || p.provider_number, medicare_lookup: lk });
        }
      } else {
        const r = await axios.get(`${API}/medicare/search`, {
          params: { surname: p.last_name || '', first_name: p.first_name || '', search_aussie: true },
          withCredentials: true,
        });
        if ((r.data.results || []).length === 1) {
          setPrac(idx, 'provider_number', r.data.results[0].provider_number);
          setPicker(null);
        } else if ((r.data.results || []).length > 1) {
          setPicker({ results: r.data.results, total: r.data.records_total });
        } else {
          setErr(`No match for "${p.first_name || ''} ${p.last_name || ''}".`);
        }
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Lookup failed.');
    }
    setBusy(false);
  };

  const pick = (row) => {
    setPrac(idx, 'provider_number', row.provider_number);
    if (onPracReplaced) {
      onPracReplaced({
        ...p,
        provider_number: row.provider_number,
        medicare_lookup: { status: 'manual', primary_practitioner_type: row.practitioner_type, primary_address: row.address },
      });
    }
    setPicker(null);
  };

  return (
    <div data-testid={`ref-prac-pn-block-${idx}`}>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5">
            Provider Number
            {reviewCount > 0 && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"
                style={{ backgroundColor: '#D9770615', color: '#D97706' }}
                data-testid={`ref-prac-pn-review-${idx}`}>
                <AlertTriangle className="w-2.5 h-2.5" /> {reviewCount} matches
              </span>
            )}
          </span>
          <button type="button" onClick={lookup} disabled={busy || (!p.last_name && !p.first_name && !p.name)}
            data-testid={`ref-prac-pn-lookup-${idx}`}
            title="Search the public RCH Medicare provider directory using the practitioner's first + last name"
            className="text-[10px] font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1 disabled:opacity-40">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
            {busy ? 'Searching…' : 'Look up'}
          </button>
        </span>
        <input type="text" value={p.provider_number || ''}
          onChange={e => setPrac(idx, 'provider_number', e.target.value)}
          data-testid={`ref-prac-pn-${idx}`}
          className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
      </label>

      {err && (
        <div className="mt-1 text-[11px] text-red-600" data-testid={`ref-prac-pn-error-${idx}`}>{err}</div>
      )}

      {picker && (
        <div className="mt-2 border border-border rounded-lg overflow-hidden bg-card" data-testid={`ref-prac-pn-picker-${idx}`}>
          <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/40 flex items-center justify-between">
            <span>{picker.total} candidates · pick the right person</span>
            <button onClick={() => setPicker(null)} className="text-muted-foreground hover:text-foreground" data-testid={`ref-prac-pn-picker-close-${idx}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-muted/20 sticky top-0">
                <tr className="text-left">
                  <th className="px-2 py-1 text-[9px] uppercase tracking-wider text-muted-foreground">Name</th>
                  <th className="px-2 py-1 text-[9px] uppercase tracking-wider text-muted-foreground">Provider #</th>
                  <th className="px-2 py-1 text-[9px] uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="px-2 py-1 text-[9px] uppercase tracking-wider text-muted-foreground">Address</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(picker.results || []).map((row, ri) => (
                  <tr key={`${row.provider_number}-${ri}`} className="hover:bg-muted/10" data-testid={`ref-prac-pn-cand-${idx}-${row.provider_number}`}>
                    <td className="px-2 py-1.5 font-semibold">{row.surname} {row.given_names}</td>
                    <td className="px-2 py-1.5 font-mono font-bold">{row.provider_number}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{row.practitioner_type}</td>
                    <td className="px-2 py-1.5 text-muted-foreground truncate max-w-xs" title={row.address}>{row.address}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => pick(row)}
                        data-testid={`ref-prac-pn-pick-${idx}-${row.provider_number}`}
                        className="text-[10px] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white"
                        style={{ backgroundColor: '#0891B2' }}>
                        <CheckCircle2 className="w-3 h-3" /> Pick
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * Bulk RCH auto-fill button — runs the RCH lookup across every practitioner
 * in the (filtered) base whose provider_number is missing. Synchronous on
 * the wire (the backend keeps the request open for the full duration), so
 * the UI shows a clear "this may take 1-3 min" disclaimer + the result
 * summary once done.
 *
 * Behaviour:
 *  • Click → 2-step confirm (don't want a clinic admin accidentally hammering
 *    RCH). Shows the active category filter so the PM can scope-down before
 *    firing.
 *  • Result summary lists auto_filled / needs_review / no_match / errored.
 *  • onDone() reloads the underlying referrer list so green chips appear.
 */
function BulkAutoFillButton({ activeCat, onDone, R }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    setBusy(true); setErr(null); setSummary(null);
    try {
      const r = await axios.post(`${API}/referrals/practitioners/auto-fill`,
        null, { params: { category: activeCat || '', only_missing: true }, withCredentials: true, timeout: 0 });
      setSummary(r.data);
      onDone?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Bulk auto-fill failed.');
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => { setOpen(true); setConfirm(false); setSummary(null); setErr(null); }}
        data-testid="referrals-bulk-rch-btn"
        title="Run the RCH Medicare lookup across every practitioner that's missing a provider number."
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl border"
        style={{ borderColor: `${R.color}40`, color: R.color }}>
        <Stethoscope className="w-4 h-4" /> Auto-fill provider #s
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="referrals-bulk-rch-modal">
          <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : () => setOpen(false)} />
          <div className="relative bg-background border border-border rounded-2xl w-full max-w-lg p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-2 mb-4 pb-3 border-b border-border">
              <h3 className="font-['Outfit'] text-base font-bold text-foreground inline-flex items-center gap-2">
                <Stethoscope className="w-4 h-4" style={{ color: R.color }} />
                Bulk auto-fill provider numbers
              </h3>
              <button onClick={() => setOpen(false)} disabled={busy} className="text-muted-foreground hover:text-foreground p-1"><X className="w-4 h-4" /></button>
            </div>

            {!summary && !confirm && (
              <>
                <p className="text-xs text-foreground/80 mb-3">
                  This will scan every practitioner in the {activeCat ? <strong>{activeCat}</strong> : 'entire'} referrer base whose provider number is empty,
                  call the public RCH Medicare provider lookup with their first/last name, and:
                </p>
                <ul className="text-xs space-y-1 list-disc pl-5 text-foreground/80 mb-3">
                  <li><strong>Auto-fill</strong> when exactly 1 result is found.</li>
                  <li><strong>Mark as &quot;needs review&quot;</strong> with the candidate list when there are multiple matches — you pick from the practitioner editor.</li>
                  <li>Skip practitioners that already have a provider number.</li>
                </ul>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-700 mb-3 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    With ~400 clinics and several practitioners each this may take <strong>1-3 minutes</strong>. Don&apos;t close this window — the request stays open for the full duration. Capped at 500 RCH calls per run.
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-lg border border-border" data-testid="referrals-bulk-rch-cancel">Cancel</button>
                  <button onClick={() => setConfirm(true)} data-testid="referrals-bulk-rch-continue"
                    className="text-sm font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5"
                    style={{ backgroundColor: R.color }}>
                    Continue <Stethoscope className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            )}

            {confirm && !summary && (
              <>
                <p className="text-xs text-foreground/80 mb-3">
                  Ready to run. The button below will start the lookup. Closing this window will not abort the server-side run, but you won&apos;t see the summary.
                </p>
                {err && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-600 mb-3" data-testid="referrals-bulk-rch-error">{err}</div>}
                <div className="flex justify-between items-center gap-2">
                  <button onClick={() => setConfirm(false)} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">← Back</button>
                  <button onClick={run} disabled={busy} data-testid="referrals-bulk-rch-go"
                    className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5"
                    style={{ backgroundColor: R.color }}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5" />}
                    {busy ? 'Running… (please wait)' : 'Run RCH lookup'}
                  </button>
                </div>
              </>
            )}

            {summary && (
              <div data-testid="referrals-bulk-rch-summary">
                <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-xs text-green-700 mb-3 inline-flex items-start gap-1.5">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Done — RCH calls used: <strong>{summary.rch_calls_used}</strong> / {summary.rch_call_cap}</span>
                </div>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-border">
                    <tr><td className="py-1.5 text-muted-foreground">Clinics scanned</td><td className="py-1.5 text-right font-bold tabular-nums">{summary.clinics_scanned}</td></tr>
                    <tr><td className="py-1.5 text-muted-foreground">Practitioners scanned</td><td className="py-1.5 text-right font-bold tabular-nums">{summary.practitioners_scanned}</td></tr>
                    <tr><td className="py-1.5 text-green-700">Auto-filled</td><td className="py-1.5 text-right font-bold tabular-nums text-green-700">{summary.auto_filled}</td></tr>
                    <tr><td className="py-1.5 text-amber-600">Needs review</td><td className="py-1.5 text-right font-bold tabular-nums text-amber-600">{summary.needs_review}</td></tr>
                    <tr><td className="py-1.5 text-muted-foreground">No match</td><td className="py-1.5 text-right font-bold tabular-nums">{summary.no_match}</td></tr>
                    <tr><td className="py-1.5 text-red-600">Errored</td><td className="py-1.5 text-right font-bold tabular-nums text-red-600">{summary.errored}</td></tr>
                    <tr><td className="py-1.5 text-muted-foreground">Skipped (already had #)</td><td className="py-1.5 text-right font-bold tabular-nums">{summary.skipped}</td></tr>
                  </tbody>
                </table>
                <div className="flex justify-end mt-3">
                  <button onClick={() => setOpen(false)} className="text-sm font-semibold px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: R.color }} data-testid="referrals-bulk-rch-close">Close</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}




// ─────────────────────────────────────────────────────────────────────────────
// FOCUS MODE DRAWER
// One scrollable side-drawer listing every practitioner missing the active ID
// field. Each row supports inline edit, save (PATCH), and — for the
// provider-number field — an RCH lookup with candidate picker.
// ─────────────────────────────────────────────────────────────────────────────

const FIELD_META = {
  provider_number: { label: 'Provider Number', short: 'PN', color: '#DC2626',
    placeholder: 'e.g. 1234567A', supportsLookup: true },
  ahpra: { label: 'AHPRA registration', short: 'AHPRA', color: '#0891B2',
    placeholder: 'e.g. MED0001234567', supportsLookup: false, dbKey: 'ahpra_id' },
  healthlink: { label: 'HealthLink ID', short: 'HL', color: '#D97706',
    placeholder: 'e.g. abcdef0', supportsLookup: false, dbKey: 'healthlink_id' },
};

function FocusModeDrawer({ field, onClose, R }) {
  const meta = FIELD_META[field] || FIELD_META.provider_number;
  const dbKey = meta.dbKey || 'provider_number';
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState('');
  const [completed, setCompleted] = useState(0);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/referrals/missing-practitioners`,
          { params: { field }, withCredentials: true });
        if (!cancelled) setRows(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load missing list.');
      }
    })();
    return () => { cancelled = true; };
  }, [field]);

  // Esc to close · ↑/↓ to navigate between rows · Enter to save · Alt+L to lookup.
  // The per-row arrow/lookup keys are bound on the row's input (search input
  // is excluded so the PM can still type filter text without the list scrolling).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Auto-focus the first row's input once rows arrive — saves the PM a click.
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const t = setTimeout(() => {
      const first = document.querySelector('[data-focus-row-input="1"]');
      if (first) first.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [rows]);

  const filtered = (rows || []).filter(r => {
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return [r.clinic_name, r.suburb, r.first_name, r.last_name, r.name, r.specialty]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(s));
  });

  // When a row is saved, drop it locally so the list visibly shrinks.
  const onRowSaved = (rid, idx) => {
    setRows(prev => (prev || []).filter(r => !(r.referrer_id === rid && r.idx === idx)));
    setCompleted(c => c + 1);
  };

  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="focus-mode-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="relative ml-auto h-full w-full sm:w-[640px] bg-background border-l border-border shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
              <Sparkles className="w-3 h-3" /> Focus mode
            </div>
            <h3 className="font-['Outfit'] text-2xl font-bold text-foreground mt-1">
              Missing {meta.label}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {rows == null
                ? 'Loading practitioners…'
                : `${filtered.length} practitioner${filtered.length === 1 ? '' : 's'} remaining${completed ? ` · ${completed} fixed this session` : ''}`}
            </p>
          </div>
          <button onClick={onClose} data-testid="focus-mode-close"
            className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg border border-border">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search box */}
        <div className="px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              data-testid="focus-mode-search"
              placeholder="Filter this list — by clinic, suburb, or practitioner name"
              className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground" />
            {search && <button onClick={() => setSearch('')} className="text-[11px] text-muted-foreground hover:text-foreground">clear</button>}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
          {err && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-600">{err}</div>
          )}
          {rows == null && !err && (
            <div className="text-center py-12 text-sm text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {rows && filtered.length === 0 && (
            <div className="text-center py-12 text-sm text-muted-foreground" data-testid="focus-mode-empty">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-600" />
              {rows.length === 0
                ? `No practitioners are missing their ${meta.label}. Beautifully clean.`
                : 'No matches for your filter — try a different name or clear the filter.'}
            </div>
          )}
          {(rows || []).length > 0 && filtered.map(row => (
            <FocusRow key={`${row.referrer_id}-${row.idx}`} row={row} meta={meta} dbKey={dbKey}
              onSaved={() => onRowSaved(row.referrer_id, row.idx)}
              R={R} />
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-foreground" data-testid="focus-mode-shortcuts">
            <span className="font-semibold text-foreground">↑ ↓</span> rows ·
            {' '}<span className="font-semibold text-foreground">Enter</span> save{meta.supportsLookup ? ' · ' : ''}
            {meta.supportsLookup && <><span className="font-semibold text-foreground">Alt + L</span> look up · </>}
            <span className="font-semibold text-foreground">Esc</span> close
          </p>
          <button onClick={onClose} data-testid="focus-mode-done"
            className="text-sm font-semibold px-3 py-1.5 rounded-lg text-white"
            style={{ backgroundColor: R.color }}>
            Done
          </button>
        </div>
      </aside>
    </div>
  );
}

function FocusRow({ row, meta, dbKey, onSaved, R }) {
  const [val, setVal] = useState(row[dbKey] || '');
  const [busy, setBusy] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const displayName = row.name?.trim()
    || [row.first_name, row.last_name].filter(Boolean).join(' ').trim()
    || '(unnamed practitioner)';

  const save = async () => {
    if (!val.trim()) { setErr('Empty value.'); return; }
    setBusy(true); setErr('');
    try {
      // For AHPRA, route through the verify endpoint — it persists the
      // ahpra_id AND stamps the 6-month audit blob in one shot. Saves the
      // PM from clicking "Mark verified" separately on the editor later.
      if (dbKey === 'ahpra_id') {
        const cleaned = val.trim().toUpperCase();
        if (!/^[A-Z]{3}\d{8,11}$/.test(cleaned)) {
          throw new Error('AHPRA must be 3 letters + 8–11 digits (e.g. MED0001234567).');
        }
        await axios.post(`${API}/ahpra/verify`,
          { referrer_id: row.referrer_id, practitioner_index: row.idx, ahpra_id: cleaned, method: 'manual' },
          { withCredentials: true });
      } else {
        await axios.patch(`${API}/referrals/${row.referrer_id}/practitioners/${row.idx}`,
          { [dbKey]: val.trim() }, { withCredentials: true });
      }
      setDone(true);
      setTimeout(() => onSaved(), 350); // brief green flash before fade-out
    } catch (e) {
      setErr(e.message || e.response?.data?.detail || 'Save failed.');
    }
    setBusy(false);
  };

  const openAhpraLookup = async () => {
    // Copy the AHPRA ID (or surname fallback) to clipboard so the PM can
    // paste-search on the public register.
    const toCopy = val.trim() || row.last_name || displayName;
    if (toCopy) {
      try { await navigator.clipboard.writeText(toCopy); } catch (_e) { /* no-op */ }
    }
    window.open('https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx', '_blank', 'noopener,noreferrer');
  };

  const runLookup = async () => {
    setLookupBusy(true); setErr(''); setCandidates(null);
    try {
      const r = await axios.post(
        `${API}/referrals/${row.referrer_id}/practitioners/${row.idx}/medicare-lookup`,
        null, { withCredentials: true });
      const lookup = r.data?.lookup || {};
      const pn = r.data?.provider_number;
      if (pn) {
        setVal(pn);
        setDone(true);
        setTimeout(() => onSaved(), 600);
      } else if (lookup.alternates?.length) {
        setCandidates(lookup.alternates);
      } else {
        setErr('No matches on RCH for this surname.');
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'RCH lookup failed.');
    }
    setLookupBusy(false);
  };

  const pickCandidate = (pn) => { setVal(pn); setCandidates(null); };

  return (
    <div data-testid={`focus-row-${row.referrer_id}-${row.idx}`}
      className={`bg-card border rounded-xl p-3 transition-all ${done ? 'border-green-500/60 bg-green-500/10 opacity-70' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-foreground truncate">{displayName}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            <span className="font-semibold">{row.clinic_name}</span>
            {row.suburb && <span> · {row.suburb}</span>}
            {row.category && <span> · {row.category}</span>}
            {row.specialty && <span> · {row.specialty}</span>}
          </p>
          {row.address && (
            <p className="text-[11px] text-muted-foreground/80 truncate inline-flex items-center gap-1 mt-0.5"
              title={row.address}>
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{row.address}</span>
            </p>
          )}
        </div>
        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
          style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
          {meta.short}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <input value={val} onChange={e => { setVal(e.target.value); setDone(false); }}
          onKeyDown={e => {
            // Iter 83 — focus-mode keyboard shortcuts.
            // ↑/↓ jump to prev/next row input (skips already-saved rows because
            // they are removed from the DOM on save). Alt+L triggers Look up
            // for fields that support it. Enter still saves the current row.
            if (e.key === 'Enter') { save(); return; }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              const inputs = Array.from(document.querySelectorAll('[data-focus-row-input="1"]'));
              const idx = inputs.indexOf(e.currentTarget);
              const nextIdx = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
              const target = inputs[nextIdx];
              if (target) { target.focus(); target.select?.(); }
              return;
            }
            if (e.altKey && (e.key === 'l' || e.key === 'L')) {
              e.preventDefault();
              if (meta.supportsLookup && !lookupBusy && !busy && !done) runLookup();
            }
          }}
          disabled={busy || done}
          data-testid={`focus-input-${row.referrer_id}-${row.idx}`}
          data-focus-row-input="1"
          placeholder={meta.placeholder}
          title={`Enter saves · ↑/↓ moves between rows${meta.supportsLookup ? ' · Alt+L looks up' : ''}`}
          className="flex-1 bg-background border border-border rounded-lg px-2.5 py-1.5 text-sm font-mono text-foreground outline-none focus:border-primary disabled:opacity-50" />

        {meta.supportsLookup && (
          <button onClick={runLookup} disabled={lookupBusy || busy || done}
            data-testid={`focus-lookup-${row.referrer_id}-${row.idx}`}
            title="Query the public RCH Medicare directory using this practitioner's surname"
            className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border inline-flex items-center gap-1 disabled:opacity-50"
            style={{ borderColor: `${meta.color}40`, color: meta.color }}>
            {lookupBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Stethoscope className="w-3 h-3" />}
            Look up
          </button>
        )}

        {dbKey === 'ahpra_id' && (
          <button onClick={openAhpraLookup} disabled={busy || done}
            data-testid={`focus-ahpra-lookup-${row.referrer_id}-${row.idx}`}
            title="Copies the AHPRA # (or surname) to clipboard and opens the public AHPRA register in a new tab. Verify the result, then paste back + Save to stamp it as verified."
            className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border inline-flex items-center gap-1 disabled:opacity-50"
            style={{ borderColor: `${meta.color}40`, color: meta.color }}>
            <ExternalLink className="w-3 h-3" />
            AHPRA
          </button>
        )}

        <button onClick={save} disabled={busy || done || !val.trim()}
          data-testid={`focus-save-${row.referrer_id}-${row.idx}`}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1 disabled:opacity-50"
          style={{ backgroundColor: R.color }}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : done ? <CheckCircle2 className="w-3 h-3" /> : <Save className="w-3 h-3" />}
          {done ? 'Saved' : 'Save'}
        </button>
      </div>

      {err && <p className="text-[11px] text-red-600 mt-1.5" data-testid={`focus-err-${row.referrer_id}-${row.idx}`}>{err}</p>}

      {candidates && candidates.length > 0 && (
        <div className="mt-2 border border-border rounded-lg overflow-hidden bg-muted/30"
          data-testid={`focus-candidates-${row.referrer_id}-${row.idx}`}>
          <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted">
            {candidates.length} match{candidates.length === 1 ? '' : 'es'} from RCH — pick one
          </div>
          <div className="max-h-44 overflow-y-auto divide-y divide-border">
            {candidates.map(c => (
              <button key={c.provider_number} onClick={() => pickCandidate(c.provider_number)}
                data-testid={`focus-cand-${row.referrer_id}-${row.idx}-${c.provider_number}`}
                className="w-full text-left px-2.5 py-1.5 text-[11px] hover:bg-muted/60 flex items-center justify-between gap-2">
                <span className="min-w-0 truncate">
                  <span className="font-mono font-bold" style={{ color: meta.color }}>{c.provider_number}</span>
                  {c.practitioner_type && <span className="text-muted-foreground"> · {c.practitioner_type}</span>}
                  {c.address && <span className="text-muted-foreground"> · {c.address}</span>}
                </span>
                <span className="text-[10px] font-bold text-foreground shrink-0">Pick</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// HealthLink lookup + verify controls. Renders directly under the
// "HealthLink EDI" input. Two affordances:
//  • "Look up on HealthLink" — opens AUPD search (login required) in a new
//     tab. Pre-fills the practitioner name into the URL search-param so the
//     PM doesn't have to re-type it.
//  • "Mark verified" — once the PM has pasted the EDI back, this stamps an
//     audit blob on the practitioner via POST /api/healthlink/verify.
// When the FHIR API key arrives (env-flagged), we'll add a third
// "Auto-find" button — already wired on the backend.
// ─────────────────────────────────────────────────────────────────────────
function HealthLinkRow({ practitioner, practitionerIndex, referrerId, onVerified }) {
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  const verified = practitioner.healthlink_verified;
  const edi = (practitioner.healthlink_id || '').trim();
  const fullName = practitioner.name || [practitioner.first_name, practitioner.last_name].filter(Boolean).join(' ');

  // AUPD doesn't support a stable deep-link query param, but copying the
  // name to clipboard + opening the directory is the next-best UX.
  const openLookup = async () => {
    if (fullName) {
      try { await navigator.clipboard.writeText(fullName); } catch (_e) { /* no-op */ }
    }
    window.open('https://aupd.healthlink.net/', '_blank', 'noopener,noreferrer');
  };

  const markVerified = async () => {
    setError('');
    if (!edi) {
      setError('Enter the HealthLink EDI first, then click Mark verified.');
      return;
    }
    if (!/^[a-zA-Z0-9]{4,12}$/.test(edi)) {
      setError('EDI must be 4–12 alphanumeric chars.');
      return;
    }
    if (!referrerId) {
      // Brand-new clinic that hasn't been saved yet. Just stamp locally —
      // the audit blob will persist on first save through the parent form.
      onVerified?.({
        by: 'unsaved',
        at: new Date().toISOString(),
        method: 'manual',
        edi: edi.toLowerCase(),
      });
      return;
    }
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/healthlink/verify`,
        { referrer_id: referrerId, practitioner_index: practitionerIndex, edi, method: 'manual' },
        { withCredentials: true });
      onVerified?.(r.data.verified);
    } catch (e) {
      setError(e.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const verifiedAt = verified?.at ? new Date(verified.at).toLocaleDateString() : '';

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]">
      <button
        type="button"
        onClick={openLookup}
        data-testid={`ref-prac-hlink-lookup-${practitionerIndex}`}
        title="Opens HealthLink AUPD search in a new tab. The practitioner's name is copied to your clipboard so you can paste-search."
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300 hover:bg-amber-500/15 transition-colors"
      >
        <ExternalLink className="w-3 h-3" />
        Look up on HealthLink
      </button>
      {!verified && edi && (
        <button
          type="button"
          onClick={markVerified}
          disabled={verifying}
          data-testid={`ref-prac-hlink-verify-${practitionerIndex}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15 transition-colors disabled:opacity-50"
        >
          {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
          Mark verified
        </button>
      )}
      {verified && (
        <span
          data-testid={`ref-prac-hlink-verified-${practitionerIndex}`}
          title={`Verified by ${verified.by} on ${verifiedAt} (${verified.method})`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
        >
          <CheckCircle2 className="w-3 h-3" />
          Verified · {verifiedAt}
        </span>
      )}
      {error && (
        <span className="text-red-600 dark:text-red-400 normal-case font-normal text-[10px]">{error}</span>
      )}
    </div>
  );
}




// ─────────────────────────────────────────────────────────────────────────
// AHPRA smart-passthrough row. Pattern mirrors HealthLinkRow but adds an
// expiry-aware badge: green within 6 months of verify, amber after.
// AHPRA registrations renew yearly so a 6-month re-check cycle keeps DIAS
// audits comfortable.
// ─────────────────────────────────────────────────────────────────────────
function AhpraRow({ practitioner, practitionerIndex, referrerId, onVerified }) {
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  const verified = practitioner.ahpra_verified;
  const ahpra = (practitioner.ahpra_id || '').trim().toUpperCase();
  const fullName = practitioner.name || [practitioner.first_name, practitioner.last_name].filter(Boolean).join(' ');

  const expiresAt = verified?.expires_at ? new Date(verified.expires_at) : null;
  const verifiedAt = verified?.at ? new Date(verified.at) : null;
  const now = new Date();
  const isStale = expiresAt && expiresAt < now;
  const verifiedDateStr = verifiedAt ? verifiedAt.toLocaleDateString() : '';

  const openLookup = async () => {
    // AHPRA's register doesn't accept URL params (POST form), so the next
    // best UX is to copy the AHPRA # (or name fallback) to clipboard and
    // open the public register search.
    const toCopy = ahpra || fullName;
    if (toCopy) {
      try { await navigator.clipboard.writeText(toCopy); } catch (_e) { /* no-op */ }
    }
    window.open('https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx', '_blank', 'noopener,noreferrer');
  };

  const markVerified = async () => {
    setError('');
    if (!ahpra) {
      setError('Enter the AHPRA ID first, then click Mark verified.');
      return;
    }
    if (!/^[A-Z]{3}\d{8,11}$/.test(ahpra)) {
      setError('Format must be 3 letters + 8–11 digits (e.g. MED0001234567).');
      return;
    }
    if (!referrerId) {
      const expires = new Date(); expires.setMonth(expires.getMonth() + 6);
      onVerified?.({ by: 'unsaved', at: now.toISOString(), expires_at: expires.toISOString(), method: 'manual', ahpra_id: ahpra });
      return;
    }
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/ahpra/verify`,
        { referrer_id: referrerId, practitioner_index: practitionerIndex, ahpra_id: ahpra, method: 'manual' },
        { withCredentials: true });
      onVerified?.(r.data.verified);
    } catch (e) {
      setError(e.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]">
      <button
        type="button"
        onClick={openLookup}
        data-testid={`ref-prac-ahpra-lookup-${practitionerIndex}`}
        title="Copies the AHPRA # to your clipboard and opens the public AHPRA register in a new tab."
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold uppercase tracking-wider text-sky-700 dark:text-sky-300 hover:bg-sky-500/15 transition-colors"
      >
        <ExternalLink className="w-3 h-3" />
        Look up on AHPRA
      </button>
      {!verified && ahpra && (
        <button
          type="button"
          onClick={markVerified}
          disabled={verifying}
          data-testid={`ref-prac-ahpra-verify-${practitionerIndex}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15 transition-colors disabled:opacity-50"
        >
          {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
          Mark verified
        </button>
      )}
      {verified && !isStale && (
        <span
          data-testid={`ref-prac-ahpra-verified-${practitionerIndex}`}
          title={`Verified by ${verified.by} on ${verifiedDateStr}. Re-verify by ${expiresAt ? expiresAt.toLocaleDateString() : '?'}.`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
        >
          <CheckCircle2 className="w-3 h-3" />
          Verified · {verifiedDateStr}
        </span>
      )}
      {verified && isStale && (
        <button
          type="button"
          onClick={markVerified}
          disabled={verifying}
          data-testid={`ref-prac-ahpra-restale-${practitionerIndex}`}
          title={`Verified by ${verified.by} on ${verifiedDateStr}. Re-verify due since ${expiresAt.toLocaleDateString()}. Click to re-verify now.`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-500/25 transition-colors"
        >
          <AlertTriangle className="w-3 h-3" />
          Re-verify · last {verifiedDateStr}
        </button>
      )}
      {error && (
        <span className="text-red-600 dark:text-red-400 normal-case font-normal text-[10px]">{error}</span>
      )}
    </div>
  );
}
